import * as React from 'react';
import { cn } from '../lib/utils';
import {
  getTableAlignClass,
  getTableContentClass,
  isTableDataRole,
  resolveTableAlign,
  type TableAlign,
  type TableRole,
} from '../lib/table-role';

interface SemanticTableCellProps {
  tableRole?: TableRole | undefined;
  align?: TableAlign | undefined;
}

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> & SemanticTableCellProps;
type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & SemanticTableCellProps;

function SemanticCellContent({
  align,
  dataRole,
  children,
}: {
  align: TableAlign;
  dataRole: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      data-table-content=""
      data-table-align={align}
      className={getTableContentClass(align, dataRole)}
    >
      {children}
    </div>
  );
}

function maybeSemanticContent(
  tableRole: TableRole | undefined,
  alignOverride: TableAlign | undefined,
  children: React.ReactNode
) {
  if (tableRole == null && alignOverride == null) {
    return children;
  }
  const meta = { role: tableRole, align: alignOverride };
  return (
    <SemanticCellContent align={resolveTableAlign(meta)} dataRole={isTableDataRole(meta)}>
      {children}
    </SemanticCellContent>
  );
}

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="crm-table__viewport relative w-full overflow-x-auto">
      <table ref={ref} className={cn('crm-table w-full caption-bottom text-sm', className)} {...props} />
    </div>
  )
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn('crm-table__head [&_tr]:border-b', className)} {...props} />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('crm-table__body [&_tr:last-child]:border-0', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-border bg-muted border-t font-medium [&>tr]:last:border-b-0', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'crm-table__row border-border hover:bg-muted/50 data-[state=selected]:bg-accent-bg border-b transition-colors',
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  TableHeadProps
>(({ className, tableRole, align, children, ...props }, ref) => {
  const resolvedAlign = resolveTableAlign({ role: tableRole, align });
  return (
  <th
    ref={ref}
    data-table-role={tableRole}
      data-table-align={tableRole != null || align != null ? resolvedAlign : undefined}
      className={cn(
        'crm-table__head-cell text-muted-foreground h-[var(--crm-table-header-height)] px-[var(--crm-table-cell-padding-x)] text-left align-middle text-xs font-medium whitespace-nowrap uppercase [&:has([role=checkbox])]:pr-0',
        getTableAlignClass(resolvedAlign),
        tableRole === 'selection' && 'crm-table__selection',
        tableRole === 'action' && 'crm-table__action',
        isTableDataRole({ role: tableRole }) && 'font-mono tabular-nums',
      className
    )}
    {...props}
  >
    {maybeSemanticContent(tableRole, align, children)}
  </th>
  );
});
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  TableCellProps
>(({ className, tableRole, align, children, ...props }, ref) => {
  const resolvedAlign = resolveTableAlign({ role: tableRole, align });
  return (
  <td
    ref={ref}
    data-table-role={tableRole}
      data-table-align={tableRole != null || align != null ? resolvedAlign : undefined}
      className={cn(
        'crm-table__cell h-[var(--crm-table-row-height)] px-[var(--crm-table-cell-padding-x)] py-0 align-middle text-[13px] whitespace-nowrap [&:has([role=checkbox])]:pr-0',
        getTableAlignClass(resolvedAlign),
        tableRole === 'selection' && 'crm-table__selection',
        tableRole === 'action' && 'crm-table__action',
        isTableDataRole({ role: tableRole }) && 'font-mono tabular-nums',
      className
    )}
    {...props}
  >
    {maybeSemanticContent(tableRole, align, children)}
  </td>
  );
});
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('text-muted-foreground mt-4 text-sm', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
